import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { euro, dt, timeOnly } from "@/lib/format";
import { PageHeader, Loading, StatusBadge, EmptyState, AuthImage } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PackageCheck, AlertTriangle, Camera, Check, ZoomIn, ZoomOut, X } from "lucide-react";
import { toast } from "sonner";

export default function Returns() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState(null);
  const [verified, setVerified] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [verify, setVerify] = useState(null); // {return, found:{}}
  const [lightbox, setLightbox] = useState(null); // {path, title}
  const [zoom, setZoom] = useState(false);

  const load = useCallback(() => {
    api.get("/returns?status=pending").then((r) => setPending(r.data));
    api.get("/returns?status=verified").then((r) => setVerified(r.data));
    api.get("/anomalies").then((r) => setAnomalies(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startVerify = (ret) => {
    const found = {};
    ret.expected_items.forEach((e) => (found[e.article_id] = e.qty));
    setVerify({ ret, found });
  };
  const submitVerify = async () => {
    try {
      const body = { verified_items: verify.ret.expected_items.map((e) => ({ article_id: e.article_id, qty: +verify.found[e.article_id] || 0 })) };
      const { data } = await api.post(`/returns/${verify.ret.id}/verify`, body);
      if (data.status === "anomaly") toast.warning(`Anomalia rilevata: ${data.anomalies.length} articolo/i`);
      else toast.success("Restituzione verificata ✓");
      setVerify(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resolve = async (an, action) => {
    try { await api.post(`/anomalies/${an.id}/resolve`, { action }); toast.success("Anomalia aggiornata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadPhoto = async (an, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/anomalies/${an.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Foto caricata"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Restituzioni" subtitle="Verifica dello sporco rientrato e gestione anomalie" />
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Da verificare {pending?.length ? `(${pending.length})` : ""}</TabsTrigger>
          <TabsTrigger value="verified" data-testid="tab-verified">Verificate</TabsTrigger>
          <TabsTrigger value="anomalies" data-testid="tab-anomalies">Anomalie</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "pending" && (!pending ? <Loading /> : pending.length === 0 ? <EmptyState icon={PackageCheck} title="Nessuna restituzione in attesa" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {pending.map((r) => (
            <Card key={r.id} className="p-5" data-testid={`return-${r.code}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-display font-bold text-[#0B2447]">{r.code}</p><p className="text-sm text-slate-500">{r.customer_name}</p></div>
                <StatusBadge status="pending" />
              </div>
              <div className="mt-3 text-sm text-slate-600 space-y-1">
                <p className="text-xs uppercase text-slate-400 font-semibold">Atteso</p>
                {r.expected_items.map((e) => <p key={e.article_id}>{e.qty}× {e.article_name}</p>)}
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-xs text-slate-400">Ricevuto ore {timeOnly(r.created_at)} · {r.operator_name}</p>
                <Button size="sm" onClick={() => startVerify(r)} data-testid={`verify-${r.code}`} className="bg-[#0EA5C4] hover:bg-[#0c8ba7]"><Check size={14} className="mr-1" />Verifica</Button>
              </div>
            </Card>
          ))}
        </div>
      ))}

      {tab === "verified" && (!verified ? <Loading /> : verified.length === 0 ? <EmptyState title="Nessuna restituzione verificata" /> : (
        <Card className="divide-y">
          {verified.map((r) => (
            <div key={r.id} className="flex items-center justify-between px-4 py-3">
              <div><p className="font-semibold text-[#0B2447]">{r.code}</p><p className="text-xs text-slate-400">{r.customer_name} · {dt(r.verified_at)}</p></div>
              <StatusBadge status="verified" />
            </div>
          ))}
        </Card>
      ))}

      {tab === "anomalies" && (!anomalies ? <Loading /> : anomalies.length === 0 ? <EmptyState icon={AlertTriangle} title="Nessuna anomalia" /> : (
        <div className="space-y-3">
          {anomalies.map((a) => (
            <Card key={a.id} className="p-4" data-testid={`anomaly-${a.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="font-semibold text-[#0B2447]">{a.article_name} · {a.qty} pz</p>
                  <p className="text-xs text-slate-400">{a.customer_name} · {a.type === "missing" ? "Mancante" : a.type} · {dt(a.created_at)}</p>
                  {a.notes && <p className="text-xs text-slate-500 mt-1">{a.notes}</p>}
                </div>
                <p className="font-bold text-red-600">{euro(a.value_cents)}</p>
                <StatusBadge status={a.status} />
              </div>
              {a.photo_path && (
                <button type="button" onClick={() => { setLightbox({ path: a.photo_path, title: a.article_name }); setZoom(false); }}
                        data-testid={`photo-${a.id}`} className="group relative block mt-3 rounded-xl overflow-hidden border">
                  <AuthImage path={a.photo_path} className="h-44 w-auto max-w-full object-cover transition-transform group-hover:scale-105" alt={`Foto ${a.article_name}`} />
                  <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-md"><ZoomIn size={12} /> Ingrandisci</span>
                </button>
              )}
              {a.status === "open" && (
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => resolve(a, "deduct")} data-testid={`deduct-${a.id}`}>Scala da cauzione</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(a, "charge")}>Addebita</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(a, "waive")}>Condona</Button>
                  <Button size="sm" variant="outline" onClick={() => resolve(a, "resolve")}>Risolvi</Button>
                  <label className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md border cursor-pointer hover:bg-slate-50">
                    <Camera size={14} /> Foto
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadPhoto(a, e.target.files[0])} />
                  </label>
                  {a.photo_file_id && <span className="text-xs text-green-600 self-center">✓ foto allegata</span>}
                </div>
              )}
            </Card>
          ))}
        </div>
      ))}

      <Dialog open={!!verify} onOpenChange={(o) => !o && setVerify(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Verifica {verify?.ret.code}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">Inserisci le quantità effettivamente trovate.</p>
          <div className="space-y-2 mt-2">
            {verify?.ret.expected_items.map((e) => (
              <div key={e.article_id} className="flex items-center justify-between">
                <span className="text-sm text-slate-600">{e.article_name} <span className="text-xs text-slate-400">(atteso {e.qty})</span></span>
                <Input type="number" data-testid={`found-${e.article_id}`} className="w-24" value={verify.found[e.article_id]} onChange={(ev) => setVerify({ ...verify, found: { ...verify.found, [e.article_id]: ev.target.value } })} />
              </div>
            ))}
          </div>
          <DialogFooter><Button onClick={submitVerify} data-testid="confirm-verify" className="bg-[#16a34a] hover:bg-green-700">Conferma verifica</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-[#0B2447] border-0">
          <div className="flex items-center justify-between px-4 py-3 text-white">
            <p className="font-semibold text-sm truncate">{lightbox?.title}</p>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setZoom((z) => !z)} data-testid="lightbox-zoom" className="p-2 rounded-lg hover:bg-white/10">{zoom ? <ZoomOut size={18} /> : <ZoomIn size={18} />}</button>
              <button type="button" onClick={() => setLightbox(null)} className="p-2 rounded-lg hover:bg-white/10"><X size={18} /></button>
            </div>
          </div>
          <div className="max-h-[75vh] overflow-auto bg-black/40 cb-scroll flex items-start justify-center">
            {lightbox && (
              <AuthImage path={lightbox.path}
                className={`transition-transform duration-200 origin-top ${zoom ? "scale-[2] cursor-zoom-out" : "cursor-zoom-in max-h-[75vh] w-auto object-contain"}`}
                alt={lightbox.title} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
